import React, { ReactNode } from "react";

export interface SegmentedTab<K extends string> {
  key: K;
  label: string;
  icon?: ReactNode;
  badge?: number;
}

interface Props<K extends string> {
  tabs: SegmentedTab<K>[];
  active: K;
  onChange: (key: K) => void;
  title?: string;
  titleSuffix?: ReactNode;
  subtitle?: string;
  stripMaxWidth?: string;
  className?: string;
}

function SegmentedTabs<K extends string>({
  tabs,
  active,
  onChange,
  title,
  titleSuffix,
  subtitle,
  stripMaxWidth = "max-w-2xl",
  className = "",
}: Props<K>) {
  const cols = tabs.length;
  const gridColsClass =
    cols === 2
      ? "grid-cols-2"
      : cols === 4
      ? "grid-cols-4"
      : cols === 5
      ? "grid-cols-5"
      : "grid-cols-3";

  return (
    <div className={className}>
      {title && (
        <div className="border-b-0 bg-blue-50 px-6 py-5">
          <h2 className="mb-1 text-2xl font-bold text-gray-900">
            {title}
            {titleSuffix}
          </h2>
          {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
        </div>
      )}

      <div className="border-b border-gray-100 bg-white px-3 py-2 sm:px-6 sm:py-3">
        <nav
          className={`mx-auto grid ${stripMaxWidth} ${gridColsClass} gap-1 rounded-lg bg-gray-100 p-1`}
        >
          {tabs.map(({ key, icon, label, badge }) => {
            const isActive = active === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onChange(key)}
                title={label}
                className={`flex items-center justify-center gap-1 sm:gap-2 rounded-md px-1 py-2 sm:px-2 sm:py-2.5 text-xs sm:text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? "bg-white text-blue-700 shadow-sm"
                    : "text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                }`}
              >
                {icon && (
                  <span
                    className="hidden flex-shrink-0 sm:inline"
                    translate="no"
                  >
                    {icon}
                  </span>
                )}
                <span className="truncate">{label}</span>
                {badge !== undefined && badge > 0 && (
                  <span
                    className={`hidden sm:inline-flex flex-shrink-0 items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-xs font-bold ${
                      isActive
                        ? "bg-blue-100 text-blue-700"
                        : "bg-gray-300 text-gray-600"
                    }`}
                  >
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

export default SegmentedTabs;
